import { trim } from 'lodash';
import { micromark } from 'micromark';
import { Editor, Element as SlateElement, Node, Range, Transforms } from 'slate';
import { HistoryEditor } from 'slate-history';
import { ReactEditor } from 'slate-react';

import { isMenuTriggerKey } from 'src/data/keys';
import {
  getResourceContentTypeReferToPlaceholder,
  getSlateTextFormatFromResourceContentType,
} from 'src/data/performance';
import { getDataTransferFilesAsDataUrls } from 'src/lib/browser';
import { isImageFileType } from 'src/lib/file';
import {
  characterBefore,
  createImageNode,
  createLineBreak,
  createTextNode,
  editorIsFocusedAndHasValidSelection,
  focusEditor,
  getSlateFragmentAfterSelection,
  getSlateFragmentBeforeSelection,
  getSlateSelectionFragment,
  htmlToSlate,
  isCursorInEmptyLine,
  isEditorEmpty,
  isSlateLinkActive,
  selectEditorLine,
  SLATE_EDITOR_ACTION,
  SLATE_NODE_TYPES,
  SLATE_PROMPT_TAGS,
  SLATE_TEXT_FORMATS,
  slateToHtml,
  slateToText,
  stripMarksFromSlateValue,
  toggleSlateLink,
} from 'src/lib/slate';
import { isSpaceCharacter } from 'src/lib/string';

import { TEXT_ALIGN_TYPES } from './const';
import { createInstructionNode, getEditorActionNodeType, getEditorActionTextFormat } from './lib';

const MAX_FILE_NAME_LENGTH = 15;

export const applyPlugins = (editor, plugins, options) => {
  return plugins.reduce((e, p) => p(e, options), editor);
};

export const isEditorActionActive = (editor, action) => {
  const format = getEditorActionTextFormat(action);
  const nodeType = getEditorActionNodeType(action);
  switch (nodeType) {
    case SLATE_NODE_TYPES.BLOCK:
      return isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type');
    case SLATE_NODE_TYPES.MARK:
      return isMarkActive(editor, format);
    case SLATE_NODE_TYPES.INLINE:
      return isSlateLinkActive(editor);
    default:
      return false;
  }
};

export const isEditorActionDisabled = (editor, action) => {
  if (action === SLATE_EDITOR_ACTION.insertLink) {
    return (
      !editorIsFocusedAndHasValidSelection(editor) ||
      Editor.string(editor, editor.selection).trim().length === 0
    );
  }
  return false;
};

export const getCurrentEditorTextFormat = (editor) => {
  const { selection } = editor;
  if (!selection) {
    return null;
  }

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (node) => !Editor.isEditor(node) && SlateElement.isElement(node),
    })
  );

  return match?.[0]?.type;
};

export const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (node) =>
        !Editor.isEditor(node) && SlateElement.isElement(node) && node[blockType] === format,
    })
  );

  return !!match;
};

export const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

export const insertLink = (editor, url) => {
  if (!editor.selection) {
    return;
  }

  toggleSlateLink(editor, url);
};

export const insertBankMessage = (editor, bankMessage, isTextSelected = false) => {
  HistoryEditor.withNewBatch(editor, () => {
    removePromptTagInLine(editor);

    const newNode = {
      type: SLATE_TEXT_FORMATS.BANK_MESSAGE,
      bankMessageId: bankMessage.id,
      bankMessageBody: bankMessage.body,
      children: [createTextNode(bankMessage.name || bankMessage.label)],
    };
    const tagName =
      isTextSelected && getPromptAncestorNode(editor)?.path
        ? null
        : `refer to this ${bankMessage.subtitle.toLowerCase().trim()}:`;

    insertPromptTag(
      editor,
      {
        promptTagNode: newNode,
        tagName,
      },
      {
        addNewLine: !(isTextSelected && getPromptAncestorNode(editor)?.path),
        focus: true,
      }
    );
  });
};

export const insertResourceUrl = (editor, url, isTextSelected = false) => {
  HistoryEditor.withNewBatch(editor, () => {
    removePromptTagInLine(editor);

    const newNode = {
      type: SLATE_TEXT_FORMATS.RESOURCE_URL,
      resourceUrl: url,
      children: [createTextNode(url)],
    };
    const tagName =
      isTextSelected && getPromptAncestorNode(editor)?.path ? null : 'refer to this link: ';

    insertPromptTag(
      editor,
      {
        promptTagNode: newNode,
        tagName,
      },
      { addNewLine: !(isTextSelected && getPromptAncestorNode(editor)?.path), focus: true }
    );
  });
};

export const removeNodeFromPromptById = (editor, id, label, focus = true) => {
  const nodeInnerText = getTagInnerText(label);
  const promptNodes = document.querySelectorAll(`#${id}`);
  const promptNode = Array.from(promptNodes).find((node) => node.innerText === nodeInnerText);
  if (promptNode) {
    try {
      const range = document.createRange();
      range.selectNodeContents(promptNode);

      const slateRange = ReactEditor.toSlateRange(editor, range, {
        exactMatch: false,
      });

      Transforms.select(editor, slateRange);

      const ancestorNode = getPromptAncestorNode(editor);
      if (ancestorNode?.path) {
        Transforms.removeNodes(editor, { at: ancestorNode.path });
      }

      if (focus) {
        focusEditor(editor);
      }
    } catch {
      return null;
    }
  }
  return promptNode;
};

const FILE_NODE_TYPES = [
  SLATE_TEXT_FORMATS.PROMPT_IMAGE,
  SLATE_TEXT_FORMATS.PROMPT_DOCUMENT,
  SLATE_TEXT_FORMATS.PROMPT_AUDIO,
  SLATE_TEXT_FORMATS.PROMPT_VIDEO,
];

// `removeNodeFromPromptById` finds the node by the DOM id of its tooltip, and that id exists only
// while the tooltip is open. The resource id finds the node in the editor value at any time.
export const removeFileNodeFromPromptByResourceId = (editor, resourceId) => {
  if (!resourceId) {
    return false;
  }

  const [entry] = Editor.nodes(editor, {
    at: [],
    match: (node) =>
      FILE_NODE_TYPES.includes(node.type) &&
      String(node.children?.[0]?.id || node.id) === String(resourceId),
  });
  if (!entry) {
    return false;
  }

  Transforms.removeNodes(editor, { at: entry[1] });
  return true;
};

const removePromptTagInLine = (editor) => {
  const currentSelection = editor.selection;
  if (currentSelection?.anchor || currentSelection?.focus) {
    // set selection to line, to check if there is a prompt tag in the line
    selectEditorLine(editor);

    const childNode = getPromptChildNode(editor);

    if (childNode?.path) {
      Transforms.select(editor, {
        anchor: { path: childNode.path, offset: 0 },
        focus: { path: childNode.path, offset: 0 },
      });

      Transforms.removeNodes(editor, { at: childNode?.path });
    } else {
      // if no prompt tag is found, set the selection back to the original selection
      Transforms.setSelection(editor, currentSelection);
    }
  }
};

const getTagInnerText = (fileText) => {
  return fileText?.length > MAX_FILE_NAME_LENGTH
    ? `${fileText?.substring(0, MAX_FILE_NAME_LENGTH)}\u2026`
    : fileText;
};

export const insertFileTextToPrompt = (editor, { file, type, id }, options) => {
  HistoryEditor.withNewBatch(editor, () => {
    // Remove "prompt tag" node if we are currently in one
    const ancestorNode = getPromptAncestorNode(editor);
    if (ancestorNode?.path) {
      Transforms.removeNodes(editor, { at: ancestorNode.path });
    }

    // "refer to" node, kept apart from the text before the cursor
    const charBefore = editor.selection && characterBefore(editor, Range.start(editor.selection));
    const separator = charBefore && !isSpaceCharacter(charBefore) ? ' ' : '';
    const referText = `${separator}${getResourceContentTypeReferToPlaceholder(type)}: `;
    const nodeRefer = createTextNode(referText);

    // "file" node
    const fileText = getTagInnerText(file?.label || file?.name);
    const nodeFile = {
      type: getSlateTextFormatFromResourceContentType(type),
      children: [createTextNode(fileText, { id: file.id })],
      tooltip: file?.label || file?.name,
    };

    const nodes = [createTextNode(), nodeRefer, nodeFile, createTextNode()];
    Transforms.insertNodes(editor, nodes);
  });

  if (options?.focus) {
    focusEditor(editor);
  }
};

export const insertExternalChannelItems = (editor, items = []) => {
  HistoryEditor.withNewBatch(editor, () => {
    const preparedItems = items.map((item) => `${item.field_key}: ${item.field_value}`);
    const newNode = {
      type: SLATE_TEXT_FORMATS.EXTERNAL_CHANNEL_TEXT,
      children: [createTextNode(preparedItems.join('\n'))],
    };

    insertPromptTag(editor, { promptTagNode: newNode }, { focus: true });
  });
};

export const insertVariable = (editor, variable) => {
  const newNode = {
    type: SLATE_TEXT_FORMATS.PROMPT_VARIABLE,
    children: [
      createTextNode(variable.children[0].text, {
        title: variable.children[0].title,
        info: variable.children[0].info,
        infoTooltip: variable.children[0].infoTooltip,
      }),
    ],
  };

  const tagName = 'Refer to';

  const tagAfter = 'in this prompt';

  insertPromptTag(editor, { promptTagNode: newNode, tagName, tagAfter }, { focus: true });
};

/**
 * Insert "instruction" node to the editor
 * @param {SlateEditor} editor - The Slate editor instance
 * @param {PromptInstruction} instruction - The instruction to insert
 */
export const insertInstruction = (editor, instruction) => {
  const newNode = createInstructionNode(instruction);

  // addNewLineBefore only if the input is not empty
  const text = slateToText(editor.children);

  insertPromptTag(
    editor,
    { promptTagNode: newNode },
    { focus: true, addNewLineBefore: text.length > 0 }
  );
};

const insertPromptTag = (editor, { promptTagNode, tagName, tagAfter }, options) => {
  const ancestorNode = getPromptAncestorNode(editor);
  if (ancestorNode?.path) {
    Transforms.removeNodes(editor, { at: ancestorNode.path });
  }

  const newNodes = [];

  // Add new line before
  if (options?.addNewLineBefore) {
    const newLine = createTextNode('\n');
    newNodes.push(newLine);
  }

  // Add "refer to" node if needed
  if (tagName) {
    const tagNode = createTextNode('\n' + tagName + ' ');
    newNodes.push(tagNode);
  }

  // Main "prompt" node
  newNodes.push(promptTagNode);

  if (tagAfter) {
    const tagNode = createTextNode(' ' + tagAfter);
    newNodes.push(tagNode);
  }

  // insert separation between nodes/text
  const textNode = createTextNode(options?.addNewLine ? ' \n' : ' ');
  newNodes.push(textNode);

  Transforms.insertNodes(editor, newNodes);

  if (options?.focus) {
    setTimeout(() => {
      focusEditor(editor);
    }, 0);
  }
};

const getPromptChildNode = (editor, type) => {
  return findChildByType(editor, type ? type : SLATE_TEXT_FORMATS.PROMPT_TAG);
};

export const getPromptAncestorNode = (editor) => {
  // If ancestor is a prompt tag, bank message, or product description it should be removed.
  let ancestorNode;

  for (const nodeFormat of SLATE_PROMPT_TAGS) {
    ancestorNode = findAncestorByType(editor, nodeFormat);
    if (ancestorNode?.node !== null) {
      break;
    }
  }

  return ancestorNode;
};

export const insertNodesToEditorEnd = (editor, nodes) => {
  Transforms.insertNodes(editor, nodes, {
    at: [editor.children.length - 1],
  });
};

export const insertNodesToEditorAtSelection = (editor, nodes) => {
  const selectedNodes = getSlateSelectionFragment(editor);

  if (selectedNodes?.[0]?.type === SLATE_TEXT_FORMATS.DD_VARIATION) {
    // if paste target is a DD variation, paste is as a plain text
    const text = slateToText(nodes);
    Transforms.insertText(editor, text);
    return;
  }

  // if pasted nodes doesn't have a DD variation, just insert them in the selected cursor
  if (!nodes.some((node) => node.type === SLATE_TEXT_FORMATS.DD_VARIATION)) {
    // in case we're inserting into an empty line, use `insertNodes` as we don't want
    // to retain the current line formatting in this case, but would want to use inserted nodes formatting instead
    const shouldInheritFormatting = isEditorEmpty(editor) || !isCursorInEmptyLine(editor);
    if (shouldInheritFormatting) {
      Transforms.insertFragment(editor, nodes);
    } else {
      Transforms.insertNodes(editor, nodes);
    }
    return;
  }

  // we should calculate the score again for a variation node that got pasted
  // cause even if its a part of a variation, the score of the original variation got pasted as well
  const newNodes = nodes.map((node) =>
    node.type === SLATE_TEXT_FORMATS.DD_VARIATION
      ? { ...node, variation: { ...node.variation, shouldBeScored: true } }
      : node
  );

  const path = editor.selection && editor.selection.anchor.path.slice(0, -1);
  Transforms.insertNodes(editor, newNodes, { at: path });
};

const cleanupHtmlContent = (htmlContent) => {
  const slateValue = htmlToSlate(htmlContent);
  const htmlValue = slateToHtml(slateValue);
  return htmlValue.trim();
};

export const pasteFromClipboard = async (editor, event) => {
  event.preventDefault();
  const { clipboardData } = event;

  // 'slate' has its own handler for pasting 'slate' fragments
  if (editor.insertFragmentData(clipboardData)) {
    return;
  }

  const getPastedSlateValue = async () => {
    if (clipboardData.types.includes('Files')) {
      const files = await getDataTransferFilesAsDataUrls(clipboardData);
      const imageFiles = files.filter((file) => isImageFileType(file.type));
      if (imageFiles.length > 0) {
        return imageFiles.flatMap((file) => [
          createLineBreak(),
          createImageNode({ src: file.dataUrl }),
          createLineBreak(),
        ]);
      }
    }

    const htmlContent = clipboardData.getData('text/html');
    if (htmlContent) {
      const cleanedHtml = cleanupHtmlContent(htmlContent);
      return htmlToSlate(cleanedHtml);
    }

    const textContent = clipboardData.getData('text/plain');
    if (textContent) {
      const renderedHtml = micromark(textContent);
      return htmlToSlate(renderedHtml);
    }

    return null;
  };

  const slateValue = await getPastedSlateValue();
  if (!slateValue) {
    return;
  }

  insertNodesToEditorAtSelection(editor, slateValue);
};

export const removePrefixBeforeCaret = (editor) => {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) {
    return;
  }

  const { character, range } = getCharacterBeforeCursor(editor);
  if (isMenuTriggerKey(character)) {
    Transforms.delete(editor, { at: range });
  }
};

export const getCharacterBeforeCursor = (editor) => {
  const { selection } = editor;

  if (selection && Range.isCollapsed(selection)) {
    const beforePoint = Editor.before(editor, selection, { unit: 'character' });

    if (beforePoint) {
      const range = { anchor: beforePoint, focus: selection.anchor };
      const [character] = Editor.string(editor, range);

      return { character, range };
    }
  }

  return { character: null, range: null };
};

export const getCharacterAfterCursor = (editor) => {
  const { selection } = editor;

  if (selection && Range.isCollapsed(selection)) {
    const afterPoint = Editor.after(editor, selection, { unit: 'character' });

    if (afterPoint) {
      const range = { anchor: afterPoint, focus: selection.anchor };
      const [character] = Editor.string(editor, range);

      return { character, range };
    }
  }

  return { character: null, range: null };
};

export const unwrapCurrentNode = (editor, node, path) => {
  if (Node.isNode(node) && !Editor.isEditor(node)) {
    Transforms.unwrapNodes(editor, { at: path });
  }
};

export const findChildByType = (editor, type) => {
  try {
    const { selection } = editor;

    if (!selection) {
      return null;
    }

    // Getting the path to the node where the cursor is
    const [node, path] = Editor.node(editor, selection);

    // If we couldn't find a node, return null
    if (!path) {
      return null;
    }

    // First, go over children
    for (const child of Node.children(editor, path, { reverse: true })) {
      const [node] = child;

      if (node.type === type) {
        return { node, path: child[1] };
      }
    }

    // No children of this type, check if current node is the type return it
    if (node.type === type) {
      return path;
    }
    return { node: null, path: null };
  } catch {
    return null;
  }
};

export const findAncestorByType = (editor, type) => {
  const { selection } = editor;

  if (!selection) {
    return null;
  }

  // Getting the path to the node where the cursor is
  const [node, path] = Editor.node(editor, selection);

  // If we couldn't find a node, return null
  if (!path) {
    return null;
  }

  // First, go over ancestors
  for (const ancestorPath of Node.ancestors(editor, path, { reverse: true })) {
    const [node] = ancestorPath;

    if (node.type === type) {
      return { node, path: ancestorPath[1] };
    }
  }

  // No ancestors of this type, check if current node is the type return it
  if (node.type === type) {
    return path;
  }

  return { node: null, path: null };
};

/**
 * Prepare section content for generation. Determine "marked segment" object from the current selection
 * @param {Editor} editor - Slate editor instance
 * @returns {Object|null} - Section generation context data
 */
export const prepareSectionContentForGeneration = (editor) => {
  if (!editor?.selection) {
    return null;
  }

  // Improve marks split the leaves they cover, which would reach the generator as a formatted run
  // broken into two tags. They describe the previous rewrite, not the copy.
  const fragmentToHtml = (fragment) => trim(slateToHtml(stripMarksFromSlateValue(fragment)));

  const beforeText = fragmentToHtml(getSlateFragmentBeforeSelection(editor));
  const selectionText = fragmentToHtml(getSlateSelectionFragment(editor));
  const afterText = fragmentToHtml(getSlateFragmentAfterSelection(editor));

  return {
    markedSegment: {
      prefix: beforeText,
      markedText: selectionText,
      suffix: afterText,
    },
  };
};
